import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Mail, Lock, Loader2, ShieldCheck, KeyRound, Fingerprint } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { useLang } from "../context/LanguageContext";

const Login = ({ portal = false }) => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { login, completeMfa, loginWithPasskey } = useAuth();
  const { toast } = useToast();
  const { t } = useLang();
  const ended = new URLSearchParams(window.location.search).get("ended");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challengeToken, setChallengeToken] = useState(null);
  const [otp, setOtp] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);

  const goHome = (u) => {
    toast({ title: t("auth.welcomeBackToast"), description: t("auth.signedInAs", { name: u.businessName || u.name }) });
    const pending = localStorage.getItem("lp-post-login-grab");
    if (pending && u.role === "shopper") { navigate(`/deal/${pending}`); return; }
    navigate(u.role === "admin" ? "/admin" : u.role === "business" ? "/account" : "/browse");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password, portal ? slug : undefined);
      if (res.requiresMfa) {
        setChallengeToken(res.challengeToken);
        toast({ title: t("auth.mfaTitle"), description: t("auth.mfaMsg") });
      } else {
        goHome(res);
      }
    } catch (err) {
      toast({ title: t("auth.loginFailed"), description: err.response?.data?.detail || t("auth.invalidCreds") });
    } finally { setLoading(false); }
  };

  const passkeyLogin = async () => {
    setLoading(true);
    try {
      const u = await loginWithPasskey(email.trim() || undefined, portal ? slug : undefined);
      goHome(u);
    } catch (err) {
      const cancelled = err?.name === "NotAllowedError" || err?.name === "AbortError";
      toast({ title: cancelled ? t("pk.cancelled") : t("pk.loginFailed"),
        description: cancelled ? undefined : err.response?.data?.detail || t("pk.loginFailedNote") });
    } finally { setLoading(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await completeMfa(challengeToken, otp.trim(), trustDevice);
      goHome(u);
    } catch (err) {
      toast({ title: t("auth.loginFailed"), description: err.response?.data?.detail || t("auth.invalidCreds") });
    } finally { setLoading(false); }
  };

  const input = "w-full bg-transparent outline-none text-sm text-foreground";

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/50 px-5">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-2 mb-6">
          <img src="/lp-logo.webp" alt="LytePress" className="h-16 w-16 rounded-full border border-border shadow-sm" />
          <span className="text-xl font-extrabold text-foreground">LytePress</span>
        </Link>
        <div className="bg-card border border-border rounded-2xl shadow-xl p-8">
          {ended && !challengeToken && (
            <p className="mb-5 text-sm font-semibold text-foreground bg-secondary rounded-xl px-4 py-3" data-testid="session-ended-note">
              {ended === "idle" ? t("auth.endedIdle") : t("auth.endedRevoked")}
            </p>
          )}
          {challengeToken ? (
            <>
              <div className="flex flex-col items-center mb-5">
                <span className="h-12 w-12 rounded-2xl bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center mb-3"><ShieldCheck className="text-[#16a34a]" size={24} /></span>
                <h1 className="text-2xl font-extrabold text-foreground text-center">{t("auth.mfaTitle")}</h1>
                <p className="text-sm text-muted-foreground text-center mt-1">{t("auth.mfaMsg")}</p>
              </div>
              <form onSubmit={submitOtp} className="space-y-4">
                <div className="flex items-center gap-3 border border-border rounded-xl px-4 py-3">
                  <KeyRound size={18} className="text-muted-foreground" />
                  <input autoFocus inputMode="text" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder={t("auth.mfaPlaceholder")} className={`${input} tracking-widest`} data-testid="mfa-code-input" />
                </div>
                <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="trust-device-label">
                  <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} className="h-4 w-4 rounded border-border accent-[#16a34a]" data-testid="trust-device-checkbox" />
                  <span className="text-sm text-muted-foreground">{t("auth.trustDevice")} <span className="text-xs">{t("auth.skip2fa")}</span></span>
                </label>
                <button disabled={loading || !otp.trim()} data-testid="mfa-verify-btn" className="w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">{loading && <Loader2 className="animate-spin" size={18} />} {t("auth.verify")}</button>
              </form>
              <button onClick={() => { setChallengeToken(null); setOtp(""); }} data-testid="mfa-back-btn" className="mt-4 w-full text-center text-sm text-muted-foreground hover:text-foreground">← {t("auth.backToLogin")}</button>
            </>
          ) : (
            <>
              {portal && (
                <div className="mb-4 flex flex-col items-center gap-1.5" data-testid="staff-portal-badge">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#e7f6ec] dark:bg-[#16341f] px-3 py-1 text-xs font-bold text-[#16a34a]">
                    <ShieldCheck size={13} /> {t("auth.staffPortal")}
                  </span>
                </div>
              )}
              <h1 className="text-2xl font-extrabold text-foreground text-center mb-1">{portal ? t("auth.staffSignIn") : t("auth.login")}</h1>
              <p className="text-sm text-muted-foreground text-center mb-6">{portal ? t("auth.staffPortalNote") : t("auth.welcomeBack")}</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="flex items-center gap-3 border border-border rounded-xl px-4 py-3"><Mail size={18} className="text-muted-foreground" /><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.email")} className={input} data-testid="login-email" /></div>
                <div className="flex items-center gap-3 border border-border rounded-xl px-4 py-3"><Lock size={18} className="text-muted-foreground" /><input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.password")} className={input} data-testid="login-password" /></div>
                <button disabled={loading} data-testid="login-submit" className="w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">{loading && <Loader2 className="animate-spin" size={18} />} {t("auth.login")}</button>
              </form>
              <p className="mt-3 text-center text-sm"><Link to="/forgot-password" data-testid="forgot-password-link" className="text-[#16a34a] font-semibold hover:underline">{t("auth.forgot")}</Link></p>
              <div className="mt-4 flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">{t("auth.or")}</span>
                <span className="h-px flex-1 bg-border" />
              </div>
              <button type="button" onClick={passkeyLogin} disabled={loading} data-testid="passkey-login-btn"
                className="mt-4 w-full border border-border hover:border-[#16a34a] text-foreground font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
                <Fingerprint size={18} className="text-[#16a34a]" /> {t("pk.signIn")}
              </button>
              {!portal && (<>
              <p className="mt-4 text-center text-sm text-muted-foreground">{t("auth.newShopper")} <Link to="/signup" className="text-[#16a34a] font-semibold hover:underline">{t("auth.createFree")}</Link></p>
              <p className="mt-1 text-center text-sm text-muted-foreground">{t("auth.areBusiness")} <Link to="/register" className="text-[#16a34a] font-semibold hover:underline">{t("auth.registerBusiness")}</Link></p>
              </>)}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
